/**
 * 真人出镜页「想拍什么」框的三个稿源(0924 车 D 二期,契约 §3.4)—— 从 VideoRealCreatePage 拆出,
 * 主文件只管渲染。
 *
 *   ① 样片模板(样片墙「用这个拍」/ `?templateId=`):预填脚本 + 推荐参数;
 *   ② 脚本草稿(`?draft=`,军师「当编剧」卡片):取回填框,带人物则自动点选;
 *   ③ 追爆蓝图(`location.state.explosive`):零逐字套路投影 → 进页即调「帮我写脚本」。
 * 加上页面自己的「帮我写脚本」按钮,四处写稿都经 `fillBrief` / `useRealScriptWriter`
 * 同一套覆盖纪律(空 / 未改动的上一份种子才覆盖;见 model/realPersonIntake 头注)。
 */
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";

import type { RealPersonExplosiveHandoff } from "@/entities/explosive";
import type { CharacterDto } from "@/entities/character/api/characterService";
import {
  realPersonPickState,
  type CharacterPick,
} from "@/entities/character/ui/CharacterPickerDialog";
import { toast } from "@/shared/hooks/use-toast";
import type {
  RealScriptDraftDto,
  RealScriptRequest,
  RealScriptResponse,
  VideoTemplateDto,
} from "../../../api/videoService";
import type { FormState } from "../../../model/videoCreateTypes";
import {
  fillIndustrySlot,
  isReplaceableRealBrief,
  templateParams,
} from "../../../model/realPersonIntake";
import { useTenantIndustryName } from "../../../model/useTenantIndustryCode";
import {
  clampDurationForModel,
  clampResolutionForModel,
  realPersonModelTier,
  type RealPersonModelId,
} from "../../../model/realPersonModelTiers";
import type { RealPersonMode } from "./RealPersonAdvancedPlay";
import { useRealPersonIntake } from "./useRealPersonIntake";
import { useRealScriptWriter } from "./useRealScriptWriter";

export function useRealPersonBrief({
  form,
  setForm,
  setMode,
  pick,
  voiceoverText,
  setVoiceoverText,
  realChars,
  charactersLoading,
  onPickCharacter,
  durationValues,
  ratioValues,
}: {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  setMode: (m: RealPersonMode) => void;
  pick: CharacterPick | null;
  voiceoverText: string;
  setVoiceoverText: (v: string) => void;
  realChars: CharacterDto[];
  charactersLoading: boolean;
  onPickCharacter: (c: CharacterDto) => Promise<void>;
  durationValues: (model: RealPersonModelId) => readonly number[];
  ratioValues: readonly string[];
}) {
  const [activeTemplate, setActiveTemplate] = useState<VideoTemplateDto | null>(null);
  // 1004 T6:套样片时把「{行业}」换成本租户行业(没有行业就留槽位让用户填)
  const industryName = useTenantIndustryName();
  const [slotValues, setSlotValues] = useState<Record<string, string>>({});
  const [explosive, setExplosive] = useState<RealPersonExplosiveHandoff | null>(null);
  /** 程序上一次写进「想拍什么」框、用户还没动过的原文(覆盖纪律)。 */
  const seedRef = useRef<string | null>(null);
  const userInputRef = useRef("");
  userInputRef.current = form.userInput;

  /** 程序往框里写稿的唯一入口:守覆盖纪律,写进去的原文记为种子(之后可被再次替换)。 */
  const fillBrief = (text: string): boolean => {
    if (!isReplaceableRealBrief(userInputRef.current, seedRef.current)) return false;
    seedRef.current = text;
    userInputRef.current = text;
    setForm((s) => ({ ...s, userInput: text }));
    return true;
  };

  /** 「用这个拍」/ `?templateId=`:预填脚本 + 推荐参数,切回文案出片。 */
  const applyTemplate = (t: VideoTemplateDto) => {
    const params = templateParams(
      t,
      realPersonModelTier(form.model).value,
      ratioValues,
      durationValues
    );
    setForm((s) => {
      const model = params.model ?? realPersonModelTier(s.model).value;
      return {
        ...s,
        model,
        resolution: params.resolution ?? clampResolutionForModel(s.resolution, model),
        durationSec: params.durationSec ?? clampDurationForModel(s.durationSec, model),
        ratio: params.ratio ?? s.ratio,
      };
    });
    setMode("text");
    setActiveTemplate(t);
    setSlotValues({});
    const filled = fillBrief(fillIndustrySlot(t.briefScript, industryName));
    toast({
      title: `已套用样片「${t.name}」`,
      description: filled
        ? "脚本和参数已填好 —— 把 {产品} 这类占位改成你的内容,或填槽位后点「帮我写脚本」"
        : "参数已按样片设好;框里是你自己写的内容,没有覆盖",
    });
  };

  /** 待选中的出镜人(草稿带来的 characterId,人物列表到货后再点选)。 */
  const [wantCharacterId, setWantCharacterId] = useState<number | null>(null);

  /** 脚本回填:提示词进框;口播建议只在口播框空时放进去(不替用户打开口播开关 —— 那会加钱)。 */
  const applyScript = (r: RealScriptResponse | RealScriptDraftDto) => {
    // 回填走 useRealScriptWriter 已判过「可覆盖」(或用户点了「用这版替换」)的路径,直接写。
    seedRef.current = r.prompt;
    userInputRef.current = r.prompt;
    setForm((s) => ({ ...s, userInput: r.prompt }));
    setMode("text");
    if (r.voiceover && r.voiceover.trim() && !voiceoverText.trim()) {
      setVoiceoverText(r.voiceover.trim());
      toast({
        title: "脚本已填进「想拍什么」",
        description: "口播建议也放进了右侧口播框(口播默认关,打开才会配音)",
      });
    }
  };

  const scriptWriter = useRealScriptWriter({
    getText: () => userInputRef.current,
    getSeed: () => seedRef.current,
    apply: applyScript,
  });

  useRealPersonIntake({
    onTemplate: applyTemplate,
    onDraft: (d) => {
      if (!fillBrief(d.prompt)) {
        toast({ title: "脚本草稿没覆盖你已写的内容", description: "需要的话清空框再打开这个链接" });
      } else if (d.voiceover && d.voiceover.trim() && !voiceoverText.trim()) {
        setVoiceoverText(d.voiceover.trim());
      }
      setMode("text");
      if (typeof d.durationSec === "number" && d.durationSec > 0) {
        const steps = durationValues(realPersonModelTier(form.model).value);
        const fit = [...steps].reverse().find((v) => v <= d.durationSec!) ?? steps[0];
        setForm((s) => ({ ...s, durationSec: fit }));
      }
      if (typeof d.characterId === "number") setWantCharacterId(d.characterId);
    },
    onExplosive: (h) => {
      setExplosive(h);
      setMode("text");
      // 追爆出口:进页即按套路写一版(免费);出镜人没选也能写,选了人再点「重新写」更贴本人。
      void scriptWriter.run(
        {
          blueprintFormula: h.formula,
          characterId: pick?.character.id ?? null,
          durationSec: form.durationSec,
        },
        userInputRef.current
      );
    },
  });

  useEffect(() => {
    if (wantCharacterId == null || charactersLoading) return;
    const c = realChars.find((x) => x.id === wantCharacterId);
    setWantCharacterId(null);
    if (c && realPersonPickState(c) === "selectable") void onPickCharacter(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantCharacterId, charactersLoading]);

  /**
   * 「帮我写脚本」入参择一(契约三形态):
   *   蓝图在场且框仍是未改动的种子/空 → blueprintFormula;
   *   样片在场且框仍是未改动的种子 → templateId + slots;
   *   否则框里有字 → idea(用户自己的一句话/草稿);框空 → 提示先写一句或挑样片。
   */
  const buildScriptRequest = (): RealScriptRequest | null => {
    const base = {
      characterId: pick?.character.id ?? null,
      durationSec: form.durationSec,
    };
    const text = form.userInput.trim();
    const untouched = isReplaceableRealBrief(text, seedRef.current);
    if (explosive && untouched) return { ...base, blueprintFormula: explosive.formula };
    if (activeTemplate && untouched && text) {
      const slots = Object.fromEntries(
        Object.entries(slotValues)
          .map(([k, v]) => [k, v.trim()] as const)
          .filter(([, v]) => v)
      );
      return { ...base, templateId: activeTemplate.id, slots };
    }
    if (text) return { ...base, idea: text };
    return null;
  };

  const onWriteScript = () => {
    const req = buildScriptRequest();
    if (!req) {
      toast({
        title: "先写一句想拍什么",
        description: "比如「老板在办公室介绍公司」,或者从上面样片墙挑一条再点",
      });
      return;
    }
    void scriptWriter.run(req, form.userInput);
  };

  const onRewriteFromExplosive = () => {
    if (!explosive) return;
    void scriptWriter.run(
      {
        blueprintFormula: explosive.formula,
        characterId: pick?.character.id ?? null,
        durationSec: form.durationSec,
      },
      form.userInput
    );
  };

  return {
    activeTemplate,
    clearTemplate: () => {
      setActiveTemplate(null);
      setSlotValues({});
    },
    slotValues,
    setSlot: (k: string, v: string) => setSlotValues((m) => ({ ...m, [k]: v })),
    explosive,
    scriptWriter,
    applyTemplate,
    onWriteScript,
    onRewriteFromExplosive,
  };
}
